/**
 * GiftBench Phone module: data access for the screen-pop, call log and click-to-call.
 * Enabled per tenant with VITE_PHONE=1. Talks to the tenant's Supabase directly (RLS applies).
 */
import { getSupabaseClient } from "../atomic-crm/providers/supabase/supabase";

export const phoneEnabled: boolean = import.meta.env.VITE_PHONE === "1";

export type IncomingStatus =
  | "ringing"
  | "answered"
  | "missed"
  | "voicemail"
  | "ai";

export interface IncomingCall {
  id: number;
  asterisk_uuid: string;
  caller_number: string;
  caller_name: string | null;
  extension: string | null;
  contact_id: number | null;
  company_id: number | null;
  status: IncomingStatus;
  created_at: string;
}

export interface CallRecord {
  id: number;
  asterisk_uuid: string;
  direction: "inbound" | "outbound";
  from_number: string | null;
  to_number: string | null;
  extension: string | null;
  contact_id: number | null;
  company_id: number | null;
  handled_by: "human" | "ai" | "voicemail" | "missed" | null;
  started_at: string | null;
  ended_at: string | null;
  duration_s: number | null;
  recording_path: string | null;
  transcript: TranscriptLine[] | null;
  summary: string | null;
}

export interface TranscriptLine {
  speaker: string;
  text: string;
  t?: number;
}

export interface PopContact {
  id: number;
  first_name: string | null;
  last_name: string | null;
  title: string | null;
  company_id: number | null;
  companies: { name: string } | null;
}

export interface PopNote {
  id: number;
  text: string | null;
  date: string;
}

/** "+18475550199" -> "(847) 555-0199"; anything else is returned unchanged. */
export function formatPhone(number: string | null | undefined): string {
  if (!number) return "";
  const digits = number.replace(/\D/g, "");
  const national =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (national.length !== 10) return number;
  return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
}

/** 0 -> "0:00", 75 -> "1:15", 3725 -> "1:02:05" */
export function formatDuration(seconds: number | null | undefined): string {
  const s = Math.max(0, Math.floor(seconds ?? 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = h > 0 ? String(m).padStart(2, "0") : String(m);
  return `${h > 0 ? `${h}:` : ""}${mm}:${String(sec).padStart(2, "0")}`;
}

export function callerLabel(
  call: Pick<IncomingCall, "caller_number" | "caller_name">,
  contact?: PopContact | null,
): string {
  if (contact) {
    const name =
      `${contact.first_name ?? ""} ${contact.last_name ?? ""}`.trim();
    if (name) return name;
  }
  return call.caller_name || formatPhone(call.caller_number);
}

export async function fetchPopContact(
  contactId: number,
): Promise<PopContact | null> {
  const { data } = await getSupabaseClient()
    .from("contacts")
    .select("id, first_name, last_name, title, company_id, companies(name)")
    .eq("id", contactId)
    .maybeSingle();
  return (data as PopContact | null) ?? null;
}

export async function fetchRecentNotes(
  contactId: number,
  limit = 3,
): Promise<PopNote[]> {
  const { data } = await getSupabaseClient()
    .from("contact_notes")
    .select("id, text, date")
    .eq("contact_id", contactId)
    .order("date", { ascending: false })
    .limit(limit);
  return (data as PopNote[] | null) ?? [];
}

export async function fetchCalls(
  filter: { contact_id?: number; company_id?: number },
  limit = 20,
): Promise<CallRecord[]> {
  let q = getSupabaseClient()
    .from("calls")
    .select("*")
    .order("started_at", { ascending: false, nullsFirst: false })
    .limit(limit);
  if (filter.contact_id != null) q = q.eq("contact_id", filter.contact_id);
  if (filter.company_id != null) q = q.eq("company_id", filter.company_id);
  const { data } = await q;
  return (data as CallRecord[] | null) ?? [];
}

export async function recordingUrl(path: string): Promise<string | null> {
  const { data } = await getSupabaseClient()
    .storage.from("recordings")
    .createSignedUrl(path, 600);
  return data?.signedUrl ?? null;
}

export async function fetchMyExtension(
  salesId: number,
): Promise<string | null> {
  const { data } = await getSupabaseClient()
    .from("extensions")
    .select("extension")
    .eq("sales_id", salesId)
    .maybeSingle();
  return (data as { extension: string } | null)?.extension ?? null;
}

export async function saveMyExtension(
  salesId: number,
  extension: string,
): Promise<void> {
  const client = getSupabaseClient();
  const clean = extension.trim();
  if (!clean) {
    await client.from("extensions").delete().eq("sales_id", salesId);
    return;
  }
  const { error } = await client
    .from("extensions")
    .upsert(
      { sales_id: salesId, extension: clean },
      { onConflict: "extension" },
    );
  if (error) throw new Error(error.message);
}

export interface ClickToCallResult {
  ok: boolean;
  to?: string;
  from_ext?: string;
  message?: string;
}

export async function clickToCall(
  number: string,
  contactId?: number,
): Promise<ClickToCallResult> {
  const { data, error } =
    await getSupabaseClient().functions.invoke<ClickToCallResult>(
      "click_to_call",
      {
        body: { number, contact_id: contactId ?? null },
      },
    );
  if (error) {
    let message = error.message;
    try {
      const body = (await (error as { context?: Response }).context?.json()) as
        | { message?: string }
        | undefined;
      if (body?.message) message = body.message;
    } catch {
      // keep the generic message
    }
    return { ok: false, message };
  }
  return data ?? { ok: false, message: "No response" };
}
